import React, { useState } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { message, Modal } from "antd"
import dayjs from "dayjs"
import { ExclamationCircleOutlined, DeleteOutlined } from "@ant-design/icons"

import * as api from "../../../../api"

import { useStateValue } from "../../../_shared/context/AppStateStore"
import useRestTable from "../../../_shared/hooks/useRestTable.js"
import Table from "../../../_shared/components/Table"
import Drawer from "../../../_shared/components/Drawer"
import AppButton from "../../../_shared/components/AppButton"
import FloatingButton from "../../../_shared/components/FloatingButton"
import Popconfirm from "../../../_shared/components/Popconfirm"

import PostForm from "./PostForm"

//------------------------------------------------------------------------------

function Posts(props) {
  // -------------------------------------
  // Props destructuring
  // -------------------------------------

  // -------------------------------------
  // Hooks (e.g. useState, useMemo ...)
  // -------------------------------------

  const [{ token }] = useStateValue()

  const [posts, fetchPosts, isLoading, pagination] = useRestTable(
    "/posts",
    token
  )

  const [selelectedPost, setSelectedPost] = useState(null)

  // -------------------------------------
  // Effects
  // -------------------------------------

  // -------------------------------------
  // Component functions
  // -------------------------------------

  function getPaginationObject() {
    return {
      size: "small",
      total: posts,
      current: pagination.current,
      pageSize: pagination.pageSize,
      showSizeChanger: false,
    }
  }

  function handleTableChange(pagination) {
    fetchPosts({ pagination })
  }

  function handleNewPostClick() {
    setSelectedPost({})
  }

  async function handlePostFormSubmit(values) {
    try {
      if (setSelectedPost && selelectedPost.id) {
        await api.updatePost(selelectedPost.id, values, token)
      } else {
        await api.createPost(values, token)
      }

      fetchPosts()
      setSelectedPost(null)
      message.success("Post salvato con successo.")
    } catch (error) {
      message.error("Si è verificato un errore nel salvataggio del post.")
    }
  }

  function handleDrawerClose() {
    setSelectedPost(null)
  }

  async function onPublish(postId) {
    try {
      await api.publishPost(postId, token)

      fetchPosts()
    } catch (error) {
      console.error("ERROR - ", error)

      message.error(
        "Non è stato possibile pubblicare il post. Riprovare più tardi."
      )
    }
  }

  async function handleDeletePost(post) {
    try {
      const postId = post?.id

      if (!postId) {
        return
      }

      await api.deleteResource("/posts", token, postId)

      fetchPosts()
    } catch (error) {
      console.error("ERROR - ", error)

      message.error(
        "Non è stato possibile eliminare il post. Riprovare più tardi."
      )
    }
  }

  function onPublishConfirmRequest(postId) {
    Modal.confirm({
      title: "Sei sicuro di voler pubblicare il post?",
      icon: <ExclamationCircleOutlined />,
      content: "Verrà inviata una notifica a tutti gli utenti registrati.",
      onOk() {
        onPublish(postId)
      },
      onCancel() {},
    })
  }

  function renderRowActions(row) {
    return (
      <Popconfirm
        placement="left"
        title="Sei sicuro di voler eliminare questo post?"
        okText="Sì"
        cancelText="No"
        onCancel={(event) => {
          event.stopPropagation()
        }}
        onConfirm={(event) => {
          event.stopPropagation()
          handleDeletePost(row)
        }}
      >
        <FloatingButton
          className="delete-btn"
          onClick={(event) => event.stopPropagation()}
        >
          <DeleteOutlined className="icn" />
        </FloatingButton>
      </Popconfirm>
    )
  }

  // -------------------------------------
  // Component local variables
  // -------------------------------------

  return (
    <div className={props.className}>
      <div className="top-bar">
        <AppButton onClick={handleNewPostClick}>Nuovo post</AppButton>
      </div>

      <Table
        columns={getColumns(onPublishConfirmRequest, renderRowActions)}
        dataSource={posts}
        rowKey={(record) => `${record?.id}`}
        onRow={(record, rowIndex) => {
          return {
            onClick: (event) => {
              setSelectedPost(record)
            },
          }
        }}
        loading={isLoading}
        pagination={getPaginationObject()}
        onChange={handleTableChange}
      />

      <Drawer open={selelectedPost ? true : false} onCancel={handleDrawerClose}>
        <PostForm
          post={selelectedPost ?? null}
          onSubmit={handlePostFormSubmit}
          onCancel={handleDrawerClose}
        />
      </Drawer>
    </div>
  )
}

// ----------------------------------------------------------------------------
// Component PropTypes and default props
// ----------------------------------------------------------------------------

Posts.propTypes = {
  className: PropTypes.string.isRequired,
}

Posts.defaultProps = {}

// ----------------------------------------------------------------------------

const StyledPosts = styled(Posts)`
  & {
    .top-bar {
      margin-bottom: 20px;
    }

    .actions {
      display: flex;
      flex-direction: row;
      align-items: center;

      .publish-btn {
        margin-right: 20px;

        z-index: 2;
        pointer-events: all;
      }

      .delete-btn {
        z-index: 2;
        pointer-events: all;
      }
    }
  }
`
// ----------------------------------------------------------------------------

export default StyledPosts

// ----------------------------------------------------------------------------
// Table columns
// ----------------------------------------------------------------------------

function getColumns(onPublish, renderRowActions) {
  return [
    {
      title: "Data",
      dataIndex: "createdAt",
      key: "date",
      render: (text) => text && dayjs(text).format("DD MMMM YYYY"),
    },
    {
      title: "Titolo",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "",
      dataIndex: "id",
      key: "actions",
      //eslint-disable-next-line
      render: (id, post) => {
        return (
          post && (
            <div className="actions">
              <AppButton
                className="publish-btn"
                uxType="success"
                onClick={(event) => {
                  event.stopPropagation()
                  onPublish(id)
                }}
              >
                {!post.published ? "Pubblica" : "Aggiorna"}
              </AppButton>

              {renderRowActions(post)}
            </div>
          )
        )
      },
    },
  ]
}
